import React from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
} from '@react-pdf/renderer';
import logoImg from 'src/assets/images/ahedge_blue_logo.png';
import watermarkImg from 'src/assets/images/ahedge_icon_blue_inside.png';

// A rupee-capable sans-serif. The legacy Roboto webfont predates U+20B9 (₹)
// and would render tofu, so this work-order PDF (which mirrors the on-screen
// layout, currency symbols included) uses Noto Sans.
Font.register({
  family: 'HEFont',
  fonts: [
    {
      src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts/hinted/ttf/NotoSans/NotoSans-Regular.ttf',
    },
    {
      src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts/hinted/ttf/NotoSans/NotoSans-Italic.ttf',
      fontStyle: 'italic',
    },
    {
      src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts/hinted/ttf/NotoSans/NotoSans-Medium.ttf',
      fontWeight: 500,
    },
    {
      src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts/hinted/ttf/NotoSans/NotoSans-Bold.ttf',
      fontWeight: 700,
    },
  ],
});

const C = {
  primary: '#1B6AAF',
  primaryBg: '#F4F8FC',
  bg: '#FFFFFF',
  muted: '#F8FAFC',
  border: '#E2E8F0',
  strong: '#0F172A',
  muted_text: '#64748B',
  label: '#94A3B8',
  white: '#FFFFFF',
  green: '#0D9668',
  greenLight: '#ECFDF5',
};

const statusColor = (status) => {
  switch (status?.toUpperCase()) {
    case 'APPROVED':
      return '#16a34a';
    case 'VERIFIED':
      return '#2563eb';
    case 'PENDING':
    case 'PENDING_APPROVAL':
      return '#d97706';
    case 'REJECTED':
      return '#dc2626';
    case 'DRAFT':
      return '#64748b';
    default:
      return C.primary;
  }
};

const formatPoDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const [day, month, year] = dateStr.split('/');
    return new Date(year, month - 1, day).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const TERMS = [
  'GST as applicable. Please reference the PO number on all invoices.',
  'Payment via NEFT/RTGS within 7 days of invoice receipt unless otherwise agreed.',
  'For queries, contact billing@ahedge.in',
];

// Styles
const s = StyleSheet.create({
  // Page background = muted (like web bg-muted)
  page: {
    fontFamily: 'HEFont',
    backgroundColor: C.muted,
    paddingHorizontal: 36,
    paddingTop: 28,
    paddingBottom: 32,
  },

  watermark: {
    position: 'absolute',
    top: '26%',
    left: '22%',
    width: '56%',
    opacity: 0.035,
    zIndex: 0,
  },

  // Page title row (mirrors web's h1 section above the card)
  pageTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: 20,
    zIndex: 1,
  },
  pageTitleLeft: {},
  poNumberLabel: {
    fontSize: 8,
    fontWeight: 500,
    color: C.muted_text,
    marginBottom: 3,
    letterSpacing: 0.3,
  },
  pageTitleText: {
    fontSize: 16,
    fontWeight: 700,
    color: C.strong,
    letterSpacing: -0.8,
  },
  logo: { width: 100, height: 'auto' },

  // Card (white, border, rounded)
  card: {
    backgroundColor: C.bg,
    borderRadius: 10,
    border: `1 solid ${C.border}`,
    overflow: 'hidden',
    marginBottom: 14,
    zIndex: 1,
  },

  // Blue header strip
  blueHeader: {
    backgroundColor: C.primary,
    paddingHorizontal: 28,
    paddingVertical: 18,
    flexDirection: 'row',
    alignItems: 'center',
  },
  blueIconBox: {
    width: 34,
    height: 34,
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 11,
  },
  // FileText icon approximation: a small white rectangle with a folded corner line
  blueIconDoc: {
    width: 14,
    height: 17,
    backgroundColor: 'rgba(255,255,255,0.75)',
    borderRadius: 2,
  },
  blueHeaderTitle: {
    fontSize: 11.5,
    fontWeight: 700,
    color: C.white,
    letterSpacing: -0.3,
    marginBottom: 3,
  },
  blueHeaderPoNum: {
    fontSize: 8.5,
    color: 'rgba(255,255,255,0.6)',
    fontWeight: 400,
  },

  // From / To
  partyRow: {
    flexDirection: 'row',
    borderBottom: `1 solid ${C.border}`,
  },
  partyLeft: { flex: 1, paddingHorizontal: 28, paddingVertical: 22 },
  partySep: { width: 1, backgroundColor: C.border },
  partyRight: { flex: 1, paddingHorizontal: 28, paddingVertical: 22 },
  partyLabel: {
    fontSize: 6.5,
    fontWeight: 700,
    color: C.label,
    letterSpacing: 1.6,
    textTransform: 'uppercase',
    marginBottom: 9,
  },
  partyName: {
    fontSize: 10.5,
    fontWeight: 700,
    color: C.strong,
    marginBottom: 5,
    letterSpacing: -0.1,
  },
  partyLine: {
    fontSize: 9,
    color: C.muted_text,
    lineHeight: 1.65,
    fontWeight: 400,
  },
  partyGstin: {
    fontSize: 7.5,
    fontWeight: 500,
    color: C.muted_text,
    marginTop: 7,
    letterSpacing: 0.3,
  },

  // Date + Status meta row
  metaRow: {
    flexDirection: 'row',
    backgroundColor: C.muted,
    borderBottom: `1 solid ${C.border}`,
  },
  metaItem: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 28,
    paddingVertical: 12,
  },
  metaItemRight: {
    borderLeft: `1 solid ${C.border}`,
  },
  metaKey: {
    fontSize: 6.5,
    fontWeight: 700,
    color: C.label,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginRight: 10,
  },
  metaVal: {
    fontSize: 9.5,
    fontWeight: 600,
    color: C.strong,
  },
  statusPill: {
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: 4,
  },
  statusPillText: {
    fontSize: 6.5,
    fontWeight: 700,
    color: C.white,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },

  // Section label (inside main card)
  sectionLabelText: {
    paddingHorizontal: 28,
    paddingTop: 16,
    paddingBottom: 9,
    fontSize: 6.5,
    fontWeight: 700,
    color: C.label,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },

  // Order table (full-width within the card)
  tableHead: {
    flexDirection: 'row',
    backgroundColor: C.muted,
    paddingHorizontal: 28,
    paddingVertical: 9,
    borderTop: `1 solid ${C.border}`,
    borderBottom: `1 solid ${C.border}`,
  },
  tableRow: {
    flexDirection: 'row',
    paddingHorizontal: 28,
    paddingVertical: 13,
    borderBottom: `1 solid ${C.border}`,
    alignItems: 'flex-start',
  },
  tableRowLast: {
    flexDirection: 'row',
    paddingHorizontal: 28,
    paddingVertical: 13,
    alignItems: 'flex-start',
  },
  colHead: {
    fontSize: 6.5,
    fontWeight: 700,
    color: C.label,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  colText: {
    fontSize: 9.5,
    color: C.strong,
    fontWeight: 500,
  },
  colMuted: {
    fontSize: 9.5,
    color: C.muted_text,
    fontWeight: 400,
  },

  // Order Summary column widths (46%, 10%, 22%, 22%)
  cDesc: { flex: 4.6 },
  cNos: { flex: 1, textAlign: 'center' },
  cUnit: { flex: 2.2, textAlign: 'right' },
  cTotal: { flex: 2.2, textAlign: 'right' },

  // Annexure column widths (34%, 12%, 18%, 18%, 18%)
  cTitle: { flex: 3.4 },
  cCount: { flex: 1.2, textAlign: 'center' },
  cAmt: { flex: 1.8, textAlign: 'right' },

  // Candidate column widths
  cName: { flex: 2 },
  cPos: { flex: 2 },
  cCtc: { flex: 1.5, textAlign: 'right' },
  cFee: { flex: 1.5, textAlign: 'right' },

  // Totals (rows span full card width; value column mirrors the table's Total)
  totalsRow: {
    flexDirection: 'row',
    paddingHorizontal: 28,
    paddingVertical: 10,
    borderBottom: `1 solid ${C.border}`,
  },
  grandTotalRow: {
    flexDirection: 'row',
    paddingHorizontal: 28,
    paddingVertical: 10,
    backgroundColor: C.primaryBg,
  },
  // flex 7.8 = Description (4.6) + Nos (1) + Unit Price (2.2)
  totalKey: {
    flex: 7.8,
    fontSize: 9,
    fontWeight: 500,
    color: C.muted_text,
    textAlign: 'right',
    paddingRight: 14,
  },
  totalVal: {
    fontSize: 9.5,
    fontWeight: 600,
    color: C.strong,
    textAlign: 'right',
  },
  grandKey: {
    flex: 7.8,
    fontSize: 9.5,
    fontWeight: 700,
    color: C.strong,
    textAlign: 'right',
    paddingRight: 14,
  },
  grandVal: {
    fontSize: 11,
    fontWeight: 700,
    color: C.primary,
    textAlign: 'right',
    letterSpacing: -0.2,
  },

  // System note (bottom of main card)
  systemNote: {
    borderTop: `1 solid ${C.border}`,
    backgroundColor: C.muted,
    paddingHorizontal: 28,
    paddingVertical: 13,
  },
  systemNoteText: {
    fontSize: 8,
    color: C.muted_text,
    fontStyle: 'italic',
    textAlign: 'center',
    fontWeight: 400,
    letterSpacing: 0.1,
  },

  // Card section header (Terms, Annexure, Candidates)
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 14,
  },
  // Small geometric placeholder for FileText / Paperclip icons
  cardHeaderIcon: {
    width: 17,
    height: 17,
    borderRadius: 3,
    backgroundColor: C.muted,
    border: `1.5 solid ${C.border}`,
    marginRight: 9,
  },
  cardHeaderTitle: {
    fontSize: 10.5,
    fontWeight: 700,
    color: C.strong,
    letterSpacing: -0.1,
    flex: 1,
  },

  // Terms content
  termsContent: {
    paddingHorizontal: 20,
    paddingBottom: 20,
    paddingTop: 4,
    borderTop: `1 solid ${C.border}`,
  },
  termItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 4,
    paddingLeft: 4,
  },
  termBullet: {
    width: 4,
    height: 4,
    backgroundColor: C.border,
    borderRadius: 2,
    marginTop: 5,
    marginRight: 8,
    flexShrink: 0,
  },
  termText: {
    fontSize: 9,
    color: C.muted_text,
    lineHeight: 1.7,
    flex: 1,
    fontWeight: 400,
  },
  termsApprovalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 12,
    marginTop: 12,
    borderTop: `1 solid ${C.border}`,
  },
  termsApprovalIconBox: {
    width: 30,
    height: 30,
    backgroundColor: C.greenLight,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 11,
  },
  termsApprovalCheck: {
    fontSize: 10.5,
    color: C.green,
    fontWeight: 700,
  },
  termsApprovalName: {
    fontSize: 9.5,
    fontWeight: 600,
    color: C.strong,
    marginBottom: 2,
  },
  termsApprovalSub: {
    fontSize: 7.5,
    color: C.muted_text,
    fontWeight: 400,
  },

  // Annexure / Candidates table (inside their own cards, px-5 like web)
  subTableHead: {
    flexDirection: 'row',
    backgroundColor: C.muted,
    paddingHorizontal: 20,
    paddingVertical: 9,
    borderBottom: `1 solid ${C.border}`,
  },
  subTableRow: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 13,
    borderBottom: `1 solid ${C.border}`,
    alignItems: 'center',
  },
  subTableRowLast: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 13,
    alignItems: 'center',
  },
});

// Sub-components

const StatusPill = ({ status }) => (
  <View style={[s.statusPill, { backgroundColor: statusColor(status) }]}>
    <Text style={s.statusPillText}>{status?.replace(/_/g, ' ')}</Text>
  </View>
);

// Main component

const BillingPDF = ({ data }) => {
  const {
    poNumber,
    date,
    from,
    to,
    orderDetails,
    requirements,
    candidates,
    status,
    currency,
  } = data;
  const sym = currency === 'INR' ? '₹' : '$';

  const unitPrice =
    orderDetails.totalFee && orderDetails.positions
      ? (orderDetails.totalFee / orderDetails.positions).toLocaleString()
      : '—';

  const hasAnnexure = requirements && requirements.length > 0;
  const hasCandidates = candidates && candidates.length > 0;

  return (
    <Document>
      <Page size="A4" style={s.page}>
        {/* Watermark */}
        <View style={s.watermark} fixed>
          <Image src={watermarkImg} />
        </View>

        {/* Page title row (mirrors web's PO number + "Work Order" h1) */}
        <View style={s.pageTitleRow}>
          <Image src={logoImg} style={s.logo} />
        </View>

        {/* MAIN DOCUMENT CARD */}
        <View style={s.card}>
          {/* Blue header */}
          <View style={s.blueHeader}>
            <View>
              <Text style={s.blueHeaderTitle}>Work Order</Text>
              <Text style={s.blueHeaderPoNum}>{poNumber}</Text>
            </View>
          </View>

          {/* From / To */}
          <View style={s.partyRow}>
            <View style={s.partyLeft}>
              <Text style={s.partyLabel}>From</Text>
              <Text style={s.partyName}>{from.name}</Text>
              <Text style={s.partyLine}>{from.address}</Text>
              <Text style={s.partyLine}>{from.cityStatePin}</Text>
              <Text style={s.partyGstin}>GSTIN: {from.gstin}</Text>
            </View>
            <View style={s.partySep} />
            <View style={s.partyRight}>
              <Text style={s.partyLabel}>To</Text>
              <Text style={s.partyName}>{to.name}</Text>
              {to.address?.line1 && (
                <Text style={s.partyLine}>{to.address.line1}</Text>
              )}
              {to.address?.street && (
                <Text style={s.partyLine}>{to.address.street}</Text>
              )}
              {to.address?.city && to.address?.state && (
                <Text style={s.partyLine}>
                  {to.address.city}, {to.address.state}
                  {to.address.zipCode ? ` ${to.address.zipCode}` : ''}
                </Text>
              )}
              {to.gstin && to.gstin !== 'N/A' && (
                <Text style={s.partyGstin}>GSTIN: {to.gstin}</Text>
              )}
            </View>
          </View>

          {/* Date + Status meta row */}
          <View style={s.metaRow}>
            <View style={s.metaItem}>
              <Text style={s.metaKey}>Date</Text>
              <Text style={s.metaVal}>{formatPoDate(date)}</Text>
            </View>
            <View style={[s.metaItem, s.metaItemRight]}>
              <Text style={s.metaKey}>Status</Text>
              <StatusPill status={status} />
            </View>
          </View>

          {/* Order Summary label */}
          <Text style={s.sectionLabelText}>Order Summary</Text>

          {/* Order table */}
          <View style={s.tableHead}>
            <Text style={[s.colHead, s.cDesc]}>Description</Text>
            <Text style={[s.colHead, s.cNos]}>Nos</Text>
            <Text style={[s.colHead, s.cUnit]}>Unit Price</Text>
            <Text style={[s.colHead, s.cTotal]}>Total</Text>
          </View>
          <View style={s.tableRowLast}>
            <Text style={[s.colText, s.cDesc]}>{orderDetails.description}</Text>
            <Text style={[s.colText, s.cNos]}>{orderDetails.positions}</Text>
            <Text style={[s.colText, s.cUnit]}>
              {sym}
              {unitPrice}
            </Text>
            <Text style={[s.colText, s.cTotal]}>
              {sym}
              {orderDetails.totalFee?.toLocaleString()}
            </Text>
          </View>

          {/* Totals — value column matches the table's Total column (flex 2.2) */}
          <View style={s.totalsRow}>
            <Text style={s.totalKey}>Subtotal</Text>
            <Text style={[s.totalVal, s.cTotal]}>
              {sym}
              {orderDetails.totalFee?.toLocaleString()}
            </Text>
          </View>
          <View style={s.totalsRow}>
            <Text style={s.totalKey}>GST ({orderDetails.gstPercentage}%)</Text>
            <Text style={[s.totalVal, s.cTotal]}>
              {sym}
              {orderDetails.gstAmount?.toLocaleString()}
            </Text>
          </View>
          <View style={s.grandTotalRow}>
            <Text style={s.grandKey}>Grand Total</Text>
            <Text style={[s.grandVal, s.cTotal]}>
              {sym}
              {orderDetails.grandTotal?.toLocaleString()}
            </Text>
          </View>

          {/* System note */}
          <View style={s.systemNote}>
            <Text style={s.systemNoteText}>
              This is a system-generated Work Order. No signature required.
            </Text>
          </View>
        </View>
        {/* END MAIN CARD */}

        {/* TERMS & CONDITIONS CARD */}
        <View style={s.card}>
          <View style={s.cardHeader}>
            <Text style={s.cardHeaderTitle}>Terms &amp; Conditions</Text>
          </View>
          <View style={s.termsContent}>
            {TERMS.map((term) => (
              <View key={term} style={s.termItem}>
                <View style={s.termBullet} />
                <Text style={s.termText}>{term}</Text>
              </View>
            ))}
            <View style={s.termsApprovalRow}>
              <View style={s.termsApprovalIconBox}>
                <Text style={s.termsApprovalCheck}>✓</Text>
              </View>
              <View>
                <Text style={s.termsApprovalName}>Ahedge Procurement</Text>
                <Text style={s.termsApprovalSub}>
                  Digitally approved — no signature required
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* ANNEXURE CARD */}
        {hasAnnexure && (
          <View style={s.card} wrap={false}>
            <View style={s.cardHeader}>
              <Text style={s.cardHeaderTitle}>Annexure</Text>
            </View>
            {/* Annexure table */}
            <View style={s.subTableHead}>
              <Text style={[s.colHead, s.cTitle]}>Job Title</Text>
              <Text style={[s.colHead, s.cCount]}>Headcount</Text>
              <Text style={[s.colHead, s.cAmt]}>Est. CTC / Profile</Text>
              <Text style={[s.colHead, s.cAmt]}>Est. Total CTC</Text>
              <Text style={[s.colHead, s.cAmt]}>Est. Fee</Text>
            </View>
            {requirements.map((req, i) => (
              <View
                key={`req-${req.requirementId}`}
                style={
                  i < requirements.length - 1
                    ? s.subTableRow
                    : s.subTableRowLast
                }
              >
                <Text style={[s.colText, s.cTitle]}>{req.title}</Text>
                <Text style={[s.colText, s.cCount]}>{req.headcount}</Text>
                <Text style={[s.colText, s.cAmt]}>
                  {sym}
                  {req.estimatedCtcPerProfile?.toLocaleString()}
                </Text>
                <Text style={[s.colText, s.cAmt]}>
                  {sym}
                  {req.estimatedTotalCtc?.toLocaleString()}
                </Text>
                <Text style={[s.colText, s.cAmt]}>
                  {sym}
                  {req.estimatedFee?.toLocaleString()}
                </Text>
              </View>
            ))}
          </View>
        )}

        {/* CANDIDATES CARD */}
        {hasCandidates && (
          <View style={s.card} wrap={false}>
            <View style={s.cardHeader}>
              <Text style={s.cardHeaderTitle}>Candidate Details</Text>
            </View>
            <View style={s.subTableHead}>
              <Text style={[s.colHead, s.cName]}>Name</Text>
              <Text style={[s.colHead, s.cPos]}>Position</Text>
              <Text style={[s.colHead, s.cCtc]}>CTC</Text>
              <Text style={[s.colHead, s.cFee]}>Fee</Text>
            </View>
            {candidates.map((c, i) => (
              <View
                key={`cand-${c.name}-${c.position}`}
                style={
                  i < candidates.length - 1 ? s.subTableRow : s.subTableRowLast
                }
              >
                <Text style={[s.colText, s.cName]}>{c.name}</Text>
                <Text style={[s.colMuted, s.cPos]}>{c.position}</Text>
                <Text style={[s.colText, s.cCtc]}>
                  {sym}
                  {c.ctc?.toLocaleString()}
                </Text>
                <Text style={[s.colText, s.cFee]}>
                  {sym}
                  {c.fee?.toLocaleString()}
                </Text>
              </View>
            ))}
          </View>
        )}
      </Page>
    </Document>
  );
};

export default BillingPDF;
